import differenceInSeconds from "date-fns/differenceInSeconds";
import endOfDay from "date-fns/endOfDay";
import endOfMonth from "date-fns/endOfMonth";
import endOfQuarter from "date-fns/endOfQuarter";
import endOfWeek from "date-fns/endOfWeek";
import format from "date-fns/format";
import formatISO from "date-fns/formatISO";
import parseISO from "date-fns/parseISO";
import startOfMonth from "date-fns/startOfMonth";
import startOfQuarter from "date-fns/startOfQuarter";
import startOfWeek from "date-fns/startOfWeek";
import sub from "date-fns/sub";

export type TimePeriod =
  | { period: "ALL_TIME" | "THIS_WEEK" | "LAST_WEEK" | "THIS_MONTH" | "LAST_MONTH" | "LAST_QUARTER" | "LAST_12_MONTHS" }
  | { period: "SPECIFIC"; specificStart?: string; specificEnd?: string };

export type PeriodId = TimePeriod["period"];

export function timePeriodDescription(timePeriod: TimePeriod) {
  switch (timePeriod.period) {
    case "THIS_WEEK":
      return "this week";
    case "LAST_WEEK":
      return "last week";
    case "THIS_MONTH":
      return "this month";
    case "LAST_MONTH":
      return "last month";
    case "LAST_QUARTER":
      return "last quarter";
    case "LAST_12_MONTHS":
      return "last 12 months";
    case "ALL_TIME":
      return "all time";
    case "SPECIFIC":
      if (timePeriod.specificStart && timePeriod.specificEnd) {
        return `between ${format(parseISO(timePeriod.specificStart), "PP")} and ${format(
          parseISO(timePeriod.specificEnd),
          "PP",
        )}`;
      } else if (timePeriod.specificStart) {
        return `since ${format(parseISO(timePeriod.specificStart), "PP")}`;
      } else if (timePeriod.specificEnd) {
        return `until ${format(parseISO(timePeriod.specificEnd), "PP")}`;
      } else {
        return "all time";
      }
  }
}

export function timePeriodStart(timePeriod: TimePeriod) {
  switch (timePeriod.period) {
    case "THIS_WEEK":
      return formatISO(startOfWeek(new Date(), { weekStartsOn: 1 }));
    case "LAST_WEEK":
      return formatISO(startOfWeek(sub(new Date(), { weeks: 1 }), { weekStartsOn: 1 }));
    case "THIS_MONTH":
      return formatISO(startOfMonth(new Date()));
    case "LAST_MONTH":
      return formatISO(startOfMonth(sub(new Date(), { months: 1 })));
    case "LAST_QUARTER":
      return formatISO(startOfQuarter(sub(new Date(), { months: 3 })));
    case "LAST_12_MONTHS":
      return formatISO(sub(new Date(), { months: 12 }));
    case "SPECIFIC":
      return timePeriod.specificStart;
    case "ALL_TIME":
      return undefined;
  }
}

export function timePeriodEnd(timePeriod: TimePeriod) {
  switch (timePeriod.period) {
    case "THIS_WEEK":
      return formatISO(endOfWeek(new Date(), { weekStartsOn: 1 }));
    case "LAST_WEEK":
      return formatISO(endOfWeek(sub(new Date(), { weeks: 1 }), { weekStartsOn: 1 }));
    case "THIS_MONTH":
      return formatISO(endOfMonth(new Date()));
    case "LAST_MONTH":
      return formatISO(endOfMonth(sub(new Date(), { months: 1 })));
    case "LAST_QUARTER":
      return formatISO(endOfQuarter(sub(new Date(), { months: 3 })));
    case "LAST_12_MONTHS":
      return formatISO(new Date());
    case "SPECIFIC":
      return timePeriod.specificEnd;
    case "ALL_TIME":
      return undefined;
  }
}

export function previousTimePeriodStart(timePeriod: TimePeriod) {
  switch (timePeriod.period) {
    case "THIS_WEEK":
      return formatISO(startOfWeek(sub(new Date(), { weeks: 1 })));
    case "LAST_WEEK":
      return formatISO(startOfWeek(sub(new Date(), { weeks: 2 })));
    case "THIS_MONTH":
      return formatISO(startOfMonth(sub(new Date(), { months: 1 })));
    case "LAST_MONTH":
      return formatISO(startOfMonth(sub(new Date(), { months: 2 })));
    case "LAST_QUARTER":
      return formatISO(startOfQuarter(sub(new Date(), { months: 6 })));
    case "LAST_12_MONTHS":
      return formatISO(sub(new Date(), { months: 24 }));
    case "SPECIFIC": {
      if (!timePeriod.specificStart) {
        // if the selected time period has no start, it covers all time and there is not previous time period to compare it to
        return undefined;
      }
      const periodStart = parseISO(timePeriod.specificStart);
      // if there's no defined end, assume it's the end of the current day
      const periodEnd = timePeriod.specificEnd ? parseISO(timePeriod.specificEnd) : endOfDay(new Date());

      return formatISO(sub(periodStart, { seconds: differenceInSeconds(periodStart, periodEnd) }));
    }
    case "ALL_TIME":
      return undefined;
  }
}
